import { getLang } from "./i18n";
import { ApiError } from "./api";

/**
 * First-party statistics for the owner's dashboard. The visitor id is random and stays in this
 * browser; nothing here ever blocks or breaks the page.
 */
function visitorId(): string {
  try {
    let v = localStorage.getItem("jc.visitor");
    if (!v) {
      v = Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, "0")).join("");
      localStorage.setItem("jc.visitor", v);
    }
    return v;
  } catch {
    // Private mode: one id per page load is still better than nothing.
    return ((window as { __jcVisitor?: string }).__jcVisitor ??= `s${Math.random().toString(36).slice(2, 14)}`);
  }
}

function send(path: string, body: unknown): Promise<Response | null> {
  return fetch(path, {
    method: "POST",
    credentials: "same-origin",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).catch(() => null);
}

let lastView = "";
let firstView = true;
export function trackView(path: string): void {
  if (path === lastView) return;
  lastView = path;
  void send("/api/track/view", {
    visitor: visitorId(),
    path,
    lang: getLang(),
    ...(firstView && document.referrer ? { referrer: document.referrer } : {}),
  });
  firstView = false;
}

export function trackDownload(info: { subject?: string; file?: "soalan" | "skema" | "resit" | "lain"; exam?: string }): void {
  void send("/api/track/download", { visitor: visitorId(), path: location.pathname, ...info });
}

type ReportKind = "bug" | "download_failed" | "client_error";
export async function reportProblem(kind: ReportKind, message: string, details: Record<string, string | number | boolean | null> = {}): Promise<boolean> {
  const res = await send("/api/track/report", {
    kind,
    path: location.pathname,
    message: message.slice(0, 2000),
    details: { ...details, lang: getLang(), screen: `${window.innerWidth}x${window.innerHeight}` },
  });
  return !!res?.ok;
}

/** Browser errors go to the admin report list (at most 3 per page load; noise filtered out). */
let sent = 0;
export function watchErrors(): void {
  const report = (msg: string, stack?: string) => {
    if (sent >= 3 || !msg || /ResizeObserver|Script error\.?$|extension:\/\/|chrome-extension/i.test(msg + (stack ?? ""))) return;
    sent++;
    void reportProblem("client_error", msg, stack ? { stack: stack.slice(0, 1500) } : {});
  };
  window.addEventListener("error", (e) => report(e.message, (e.error as Error | undefined)?.stack));
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason as { message?: string; stack?: string; name?: string } | undefined;
    if (e.reason instanceof ApiError || r?.name === "AbortError") return; // handled API errors are shown on the page
    report(String(r?.message ?? r ?? "unhandledrejection"), r?.stack);
  });
}
